import ExpandableParagraph from "./ExpandableParagraph";

interface PublicationProps {
    title: string;
    journal: string;
    abstract: string;
    keywords?: string[];
    date: Date;
    link?: string;
}

const Publication = ({ title, journal, abstract, keywords, date, link }: PublicationProps) => {
    const fullAbstract = `<strong>Abstract:</strong> ${abstract}`;

    return (
        <div className="publication">
            <div className="publication__text">
                <h3>{title}</h3>
                <h4>{journal}
                    <span style={{ color: '#ffffff', fontWeight: '400' }}> / {date.toDateString().slice(4)}</span>
                </h4>

                <ExpandableParagraph text={fullAbstract} maxLength={250} />

                {keywords && (
                    <p>
                        <strong>Keywords: </strong>
                        {keywords.map((keyword, index) => (
                            <span key={index} className="publication__keyword">
                                {keyword}{index < keywords.length - 1 && ', '}
                            </span>
                        ))}
                    </p>)}

                {link &&
                    <a href={link} target="_blank" rel="noopener noreferrer">See the article</a>
                }
            </div>
        </div>
    );

}

const About = () => {
    return <section id="publications">
        <h1 className="underline-title">Publications</h1>
        <Publication
            title="Intelligent Augmented Reality System for Optimal Object Placement"
            abstract="Home furnishing requires meticulous attention to detail and
                knowledge in design and functionality. Current platforms often lack in-
                tuitive visualisation tools for furniture placement within a real space.
                Given the limitations observed in existing apps, this paper introduces an
                innovative Android application that utilizes Augmented Reality (AR).
                The app provides an intuitive environment, letting the user scan their
                room, make a furniture selection and visualise it into an optimally de-
                signed arrangement, in real life, by using the smartphone’s camera. This
                paper outlines the app’s structure and its utility, implementing interior
                design constraints defined through mathematical functions, heuristics,
                and optimization techniques like Backtracking and Simulated Annealing
                algorithms, integrated with the AR system and 3D models to offer the
                most suitable furniture layout."
            keywords={['Augmented Reality(AR)', 'Furniture Layout Generation', 'Simulated Annealing', 'Constraint Satisfaction Problem', 'Blender', 'Unity']}
            journal="KES-2024 International Conference on Intelligent Design Technologies"
            date={new Date(2024, 6, 20)}
        />
        <Publication
            title="Enhancing Web Accessibility through a Customizable Browser Extension for Users with Visual Impairments"
            abstract="We live in a time when we want applications to
                be accessible to as many users as possible, regardless of their
                age, their health problems or the diseases they suffer from.
                This paper introduces an approach to address the accessibility
                challenges faced by individuals with myopia, dyslexia, and color
                blindness through the development of a specialized browser
                plugin. The plugin empowers users to tailor their browsing
                experience at the browser level, enabling them to customize the
                presentation of information on desired web pages. By offering
                customizable features directed to specific visual impairments,
                such as font size and color contrast adjustments, the plugin
                enhances usability and promotes a better browsing experience
                for users with diverse needs. This initiative underscores the
                significance of measures in addressing digital inclusivity and
                underscores the potential of tailored technological solutions in
                advancing accessibility standards across web platforms."
            keywords={['Adapted Interfaces', 'Browser', 'Plugin', 'Myopia', 'Dyslexia', 'Color Blindness']}
            journal="BCI 2024 Conference"
            date={new Date(2024, 7, 17)}
        />
        <Publication
            title="MentalVerse.app SAFEvR protocol ACT Application development of a Safe Adaptative-Gamified Free Exposure in Virtual Reality for Acrophobia and Claustrophobia Therapy"
            abstract='Specific phobias impact over 400 million people worldwide. Digitalizing mental health could alleviate the burden. Still, although corporate-driven Metaverse is expanding rapidly, there needs to be more momentum in harnessing virtual reality exposure therapy uptake. Objective: This study aims to conceptualize, develop, and deploy a free Virtual Reality Exposure Therapy (VRET) application specifically designed for treating acrophobia and claustrophobia. This pilot study, which holds the promise of a future where mental health is more accessible and effective, explores the feasibility of leveraging transdisciplinary collaboration among specialists to create a safe, accessible, and effective VRET solution. Methods: We conducted a Delphi heuristic approach involving bioethicists, neuroscientists, and tech developers. Second, we reviewed the existing psychological theories and therapeutic strategies for addressing phobias in VR. Third, we conceptualized a thematic analysis-derived framework for a safe, adaptive-gamified free exposure to virtual reality acrophobia and Claustrophobia (SAFEvR ACT). Finally, we will overview the iterative improvements during 12 workshops and 76 weekly briefings on developmental implementations. Results: We developed the SAFEvR ACT into a proof-of-concept application freely deployed on the MentalVerse app platform. Our safety-focused approach can benefit from prevalidation perspectives within future randomized control trials. Conclusions: The resulting application derived from the SAFEvR ACT framework represents a blueprint to counter the current lack of iVR mental health uptake by offering a free VRET alternative. Future research should aim toward developing similar free platforms to lessen mental health burdens and gather quantitative data. We pledge to a call to action to fine-tune our current approach and take a stand for free digital mental health at https://MentalVeRse.app.'
            journal="Brain Sciences MDPI"
            keywords={['Virtual Reality Exposure Therapy', 'Self-Exposure', 'Gamification', 'Acrophobia', 'Claustrophobia', 'Transdisciplinary Research and Development', 'Free VRET', 'Metaverse']}
            date={new Date(2024, 6, 27)}
            link="https://www.mdpi.com/2076-3425/14/7/651"
        />
    </section>;
};

export default About;